/**
 * `/operations`: section tabs (Coleta, Inteligência, Mídia) over
 * subsections of table cards, one row per network-qualified operation.
 *
 * The tab and the subsection a row falls into are read off its own
 * `operation_key`, never a hand-maintained list: `intelligence.*` is
 * Inteligência, `media.*` is Mídia, everything else is Coleta grouped by
 * its leading network segment (`instagram.post.stats`). Inteligência
 * subsections group by task (`intelligence.chat`'s `chat`); a
 * task this screen has no heading for still renders, under its own raw
 * name, rather than dropping the row.
 */

import { useMemo, type ReactNode } from 'react';
import { MessageSquare, Newspaper, Sparkles, Image as ImageIcon, Users } from 'lucide-react';

import { Page, PageHeader } from '../../chrome/page';
import { useViewSearch } from '../../chrome/viewSearch';
import { EmptyState, LoadingState, QueryStates } from '../../chrome/state';
import { useOperationCatalogue } from '../api/queries';
import type { OperationCatalogueRow } from '../api/schemas';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { SubsectionTable, type Subsection } from './SubsectionTable';
import { t, taskOf } from './messages';

type TabId = 'coleta' | 'inteligencia' | 'midia';

const TABS: readonly TabId[] = ['coleta', 'inteligencia', 'midia'];

function tabOf(operationKey: string): TabId {
  if (operationKey.startsWith('intelligence.')) return 'inteligencia';
  if (operationKey.startsWith('media.')) return 'midia';
  return 'coleta';
}

function capitalize(word: string): string {
  const first = word.charAt(0);
  return first === '' ? word : first.toUpperCase() + word.slice(1);
}

const NETWORK_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  collection: 'Amostragem de métricas',
};

function InstagramGlyph(): ReactNode {
  return (
    <svg role="img" aria-hidden className="size-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.2c3.2 0 3.6 0 4.9.07 1.2.06 2.1.26 2.6.46.6.24 1.1.55 1.6 1.06.5.5.8 1 1.06 1.6.2.5.4 1.4.46 2.6.06 1.3.07 1.7.07 4.9s0 3.6-.07 4.9c-.06 1.2-.26 2.1-.46 2.6a4.3 4.3 0 0 1-2.66 2.66c-.5.2-1.4.4-2.6.46-1.3.06-1.7.07-4.9.07s-3.6 0-4.9-.07c-1.2-.06-2.1-.26-2.6-.46a4.3 4.3 0 0 1-2.66-2.66c-.2-.5-.4-1.4-.46-2.6C2.21 15.6 2.2 15.2 2.2 12s0-3.6.07-4.9c.06-1.2.26-2.1.46-2.6A4.3 4.3 0 0 1 5.39 1.84c.5-.2 1.4-.4 2.6-.46C9.29 1.32 9.69 1.31 12 1.31Zm0 3.03a6.77 6.77 0 1 0 0 13.54 6.77 6.77 0 0 0 0-13.54Zm0 11.17a4.4 4.4 0 1 1 0-8.8 4.4 4.4 0 0 1 0 8.8Zm7.03-11.4a1.58 1.58 0 1 1-3.16 0 1.58 1.58 0 0 1 3.16 0Z" />
    </svg>
  );
}

function TikTokGlyph(): ReactNode {
  return (
    <svg role="img" aria-hidden className="size-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.25 1.25 2.29 2.5 2.52 1.06.2 2.21-.08 2.97-.83.67-.62.97-1.54.98-2.43.02-4.52.01-9.04.01-13.56z" />
    </svg>
  );
}

function networkIcon(network: string): ReactNode {
  if (network === 'instagram') return <InstagramGlyph />;
  if (network === 'tiktok') return <TikTokGlyph />;
  return <Sparkles role="img" aria-hidden className="size-4" />;
}

/** Task subsections `659:19825` actually draws; a task the catalogue names
 *  but this table has no heading for still gets a row, under its own key,
 *  rather than being dropped (`subsection.task.fallback`). */
const TASK_SUBSECTIONS: Record<string, { heading: string; icon: ReactNode }> = {
  chat: { heading: 'Chat do agente', icon: <MessageSquare aria-hidden className="size-4" /> },
  post_access: { heading: 'Publicações', icon: <Newspaper aria-hidden className="size-4" /> },
  post_intel: { heading: 'Publicações', icon: <Newspaper aria-hidden className="size-4" /> },
  profile_account: { heading: 'Perfis', icon: <Users aria-hidden className="size-4" /> },
};

function subsectionsFor(tab: TabId, rows: OperationCatalogueRow[]): Subsection[] {
  const groups = new Map<string, Subsection>();
  for (const row of rows) {
    if (tabOf(row.operation_key) !== tab) continue;
    const segments = row.operation_key.split('.');
    let id: string;
    let heading: string;
    let icon: ReactNode;
    if (tab === 'coleta') {
      id = segments[0] ?? row.operation_key;
      heading = NETWORK_NAMES[id] ?? capitalize(id);
      icon = networkIcon(id);
    } else if (tab === 'inteligencia') {
      id = taskOf(row.operation_key) ?? row.operation_key;
      const known = TASK_SUBSECTIONS[id];
      heading = known?.heading ?? t('subsection.task.fallback');
      icon = known?.icon ?? <Sparkles aria-hidden className="size-4" />;
    } else {
      id = 'midia';
      heading = t('subsection.midia');
      icon = <ImageIcon aria-hidden className="size-4" />;
    }
    const existing = groups.get(id);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.set(id, { id, heading, icon, rows: [row] });
    }
  }
  return [...groups.values()];
}

function OperationsTabs(props: { operations: OperationCatalogueRow[] }): ReactNode {
  const [values, set] = useViewSearch('ops.operations', {
    tab: { default: 'coleta', values: TABS },
  });
  const tab = values.tab as TabId;
  const setTab = (next: TabId): void => set({ tab: next });
  const byTab = useMemo(
    () => new Map(TABS.map((id) => [id, subsectionsFor(id, props.operations)] as const)),
    [props.operations],
  );

  return (
    <Tabs value={tab} onValueChange={(value) => setTab(value as TabId)}>
      <TabsList aria-label={t('operations.title')}>
        {TABS.map((id) => (
          <TabsTrigger key={id} value={id}>
            {t(`tab.${id}`)}
          </TabsTrigger>
        ))}
      </TabsList>
      {TABS.map((id) => (
        <TabsContent key={id} value={id} className="flex flex-col gap-4">
          {(byTab.get(id) ?? []).map((subsection) => (
            <SubsectionTable key={subsection.id} subsection={subsection} />
          ))}
        </TabsContent>
      ))}
    </Tabs>
  );
}

export function Operations(): ReactNode {
  const query = useOperationCatalogue();

  return (
    <Page>
      <PageHeader title={t('nav.label')} />
      <QueryStates<OperationCatalogueRow[]>
        query={query}
        loading={<LoadingState label={t('operations.title')} />}
        empty={<EmptyState title={t('empty')} />}
      >
        {(operations) => <OperationsTabs operations={operations} />}
      </QueryStates>
    </Page>
  );
}
